import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Printer, Edit2, Trash2, Star, StarOff } from "lucide-react";
import type { PrinterWithJobTypes, JobType } from "@/hooks/usePrinters";
import { JOB_TYPE_LABELS } from "@/hooks/usePrinters";

interface PrinterListProps {
  printers: PrinterWithJobTypes[];
  onEdit: (printer: PrinterWithJobTypes) => void;
  onDelete: (printerId: string) => Promise<boolean>;
  onSetDefault: (printerId: string) => Promise<boolean>;
}

export function PrinterList({
  printers,
  onEdit,
  onDelete,
  onSetDefault,
}: PrinterListProps) {
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = async () => {
    if (deleteId) {
      await onDelete(deleteId);
      setDeleteId(null);
    }
  };

  if (printers.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Printer className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Nenhuma impressora cadastrada</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Clique em "Nova Impressora" para adicionar
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {printers.map((printer) => (
          <Card
            key={printer.id}
            className={!printer.is_active ? "opacity-60" : ""}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Printer className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">{printer.name}</CardTitle>
                  {printer.is_default && (
                    <Badge variant="secondary" className="text-xs">
                      Padrão
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!printer.is_default && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onSetDefault(printer.id)}
                      title="Definir como padrão"
                    >
                      <StarOff className="h-4 w-4" />
                    </Button>
                  )}
                  {printer.is_default && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      disabled
                    >
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit(printer)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => setDeleteId(printer.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {printer.printer_name}
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">Status:</span>
                <Badge variant={printer.is_active ? "default" : "secondary"}>
                  {printer.is_active ? "Ativa" : "Inativa"}
                </Badge>
              </div>
              {printer.job_types.length > 0 ? (
                <div className="space-y-1">
                  <span className="text-sm font-medium">Tipos de impressão:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {printer.job_types
                      .filter((jt) => jt.is_enabled)
                      .map((jt) => (
                        <Badge
                          key={jt.id}
                          variant="outline"
                          className="text-xs"
                        >
                          {JOB_TYPE_LABELS[jt.job_type as JobType]}
                        </Badge>
                      ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nenhum tipo de impressão configurado
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Impressora</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover esta impressora? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
